import {
  AppRouterInterfaceContext,
  useAppRouterInterface,
} from '@/app/addRouter';
import { useStackContextRef } from '@/components/Layout/Stack/StackContext';
import { Breakpoint, useBreakpoint } from '@/hooks/use-breakpoint';
import {
  CreateLinkProps,
  LinkComponentProps,
  UseNavigateResult,
  createLink as createRouterLink,
  useNavigate,
} from '@tanstack/react-router';
import { Constrain } from '@tanstack/router-core';
import { ReactNode, useCallback, useContext, useMemo } from 'react';
import { useSidebar } from '@/components/ui/Sidebar/SidebarContextProps';

// FIXME: dont call conditionally
// FIXME: TYPING MESS!
export function useStackNavigate(): UseNavigateResult<string> {
  // make it work in the demo context
  const appRouter = useContext(AppRouterInterfaceContext);
  if (!appRouter) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(async ({ to }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.location = to as any; //(to as any);
    }, []);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const nav = useNavigate();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const makeLink = useLinkLogic();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCallback<typeof nav>(
    ({ to, ...opts }) =>
      nav({
        to: makeLink(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          to as any,
          false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (opts as any)?.preferredStack ?? 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as any /* ???: why? */,
        search: (s) => s,
        ...opts,
      }),
    [nav, makeLink],
  );
}

export function useLinkLogic() {
  const appRouter = useAppRouterInterface();
  const breakpoint = useBreakpoint();

  const stackCtxRef = useStackContextRef();

  return useCallback(
    (to: string, invert?: false, preferredStack?: number) => {
      let targetStackIdx = stackCtxRef.current.stackIdx;
      const ctxStackIdx = stackCtxRef.current.stackIdx;
      if (breakpoint > Breakpoint.LG) {
        if (preferredStack != null) {
          targetStackIdx = preferredStack;
        }

        if (invert) {
          targetStackIdx = (ctxStackIdx + 1) % 2;
        }
      }
      return appRouter.getNavigationUrlForStack(targetStackIdx, to);
    },
    [stackCtxRef, appRouter, breakpoint],
  );
}

// TODO: proper types
export function createLink<const TComp>(
  Comp: Constrain<TComp, unknown, (props: CreateLinkProps) => ReactNode>,
) {
  const RLComponent = createRouterLink(Comp);
  return (props: LinkComponentProps<TComp> & { preferredStack?: number }) => {
    const linkLogic = useLinkLogic();
    const { isMobile, setOpenMobile } = useSidebar();

    // Log which of the stackTo deps changes on each render
    const stackTo = useMemo(
      () => linkLogic(props.to, false, props.preferredStack),
      [linkLogic, props.preferredStack, props.to],
    );

    // Close mobile drawer on navigation
    // Tech
    const handleClick = useCallback(() => {
      if (isMobile) {
        setOpenMobile(false);
      }
    }, [isMobile, setOpenMobile]);

    return (
      <RLComponent
        {...props}
        to={stackTo}
        search={(x) => x}
        onClick={handleClick}
      />
    );
  };
}
